import React, { useContext, useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, TextField, Button, MenuItem, Select, FormControl,
  InputLabel, CircularProgress, Alert, Paper, Stack, Dialog, DialogTitle,
  DialogContent, DialogActions, IconButton, Tabs, Tab, Checkbox,
  ListItemText, List, ListItem, ListItemIcon, Divider, FormControlLabel, Switch
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DragHandleIcon from '@mui/icons-material/DragHandle';
import { Editor } from '@tinymce/tinymce-react';
import { useAuth } from '../auth';
import { baseBackendUrl } from '../service/baseService';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import { contentProofreadingService } from '../service/ContentProofreadingService';
import { ProofreadIssue } from '../types/proofread';
import ProofreadReviewDialog from '../common/ProofreadReviewDialog';

interface Announcement {
  announcementId: number;
  title: string;
  description: string;
}

const SendEmail: React.FC = () => {
  const { IsAdmin, cookies } = useContext(useAuth);
  const [tabValue, setTabValue] = useState(0);
  const [mailingLists, setMailingLists] = useState<string[]>([]);
  const [selectedList, setSelectedList] = useState<string>('');

  // Regular Email State
  const [subject, setSubject] = useState<string>('');
  const [body, setBody] = useState<string>('');

  // Announcement Email State
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [selectedAnnouncementIds, setSelectedAnnouncementIds] = useState<number[]>([]);
  const [announcementSearch, setAnnouncementSearch] = useState<string>('');
  const [isScheduled, setIsScheduled] = useState<boolean>(false);
  const [scheduleDate, setScheduleDate] = useState<string>(''); // yyyy-mm-dd, from a native date input
  const [includeStaySubscribedButton, setIncludeStaySubscribedButton] = useState<boolean>(false);

  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [suggestedBody, setSuggestedBody] = useState<string>('');
  const [isRewriting, setIsRewriting] = useState<boolean>(false);
  const [previewOpen, setPreviewOpen] = useState<boolean>(false);
  const [previewHtml, setPreviewHtml] = useState<string>('');
  const [previewLoading, setPreviewLoading] = useState<boolean>(false);

  // Pre-send proofread check state - invisible unless Grok finds something worth flagging.
  const [isCheckingContent, setIsCheckingContent] = useState<boolean>(false);
  const [proofreadIssues, setProofreadIssues] = useState<ProofreadIssue[]>([]);
  const [proofreadDialogOpen, setProofreadDialogOpen] = useState<boolean>(false);

  useEffect(() => {
    const fetchData = async () => {
      if (!cookies?.['UserAuth']?.token) {
        setError('Authentication token not found');
        return;
      }

      const requestOptions: RequestInit = {
        headers: {
          'Authorization': `Bearer ${cookies['UserAuth'].token}`
        }
      };

      try {
        // Fetch Mailing Lists
        const mlResponse = await fetch(`${baseBackendUrl}/api/email/Get-Available-Mailing-Lists`, requestOptions);
        if (!mlResponse.ok) throw new Error('Failed to fetch mailing lists');
        const mlData: string[] = await mlResponse.json();
        setMailingLists(mlData);

        // Fetch Announcements
        const annResponse = await fetch(`${baseBackendUrl}/api/announcement`, requestOptions);
        if (!annResponse.ok) throw new Error('Failed to fetch announcements');
        const annData: Announcement[] = await annResponse.json();
        setAnnouncements(annData);
      } catch (err) {
        setError('Failed to load initial data');
      }
    };

    fetchData();
  }, [cookies]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setSuccess('');
    setError('');
    setSuggestedBody('');
    setIsScheduled(false);
    setScheduleDate('');
  };

  const handleAnnouncementToggle = (id: number) => {
    setSelectedAnnouncementIds(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const handleDragStart = (e: React.DragEvent, index: number) => {
    e.dataTransfer.setData('index', index.toString());
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDrop = (e: React.DragEvent, targetIndex: number) => {
    const sourceIndex = parseInt(e.dataTransfer.getData('index'));
    const newOrder = [...selectedAnnouncementIds];
    const [movedItem] = newOrder.splice(sourceIndex, 1);
    newOrder.splice(targetIndex, 0, movedItem);
    setSelectedAnnouncementIds(newOrder);
  };

  const performSend = async (bodyToSend: string) => {
    setLoading(true);
    setError('');
    setSuccess('');

    if (!cookies?.['UserAuth']?.token) {
      setError('Authentication token not found');
      setLoading(false);
      return;
    }

    const isAnnouncement = tabValue === 1;

    try {
      const endpoint = isAnnouncement
        ? `${baseBackendUrl}/api/email/send-announcement-email`
        : `${baseBackendUrl}/api/email/send-email`;

      const payload = isAnnouncement ? {
        title: subject,
        body: bodyToSend,
        mailingListName: selectedList,
        announcementIds: selectedAnnouncementIds,
        dateToSend: isScheduled && scheduleDate ? scheduleDate : null
      } : {
        mailingListName: selectedList,
        subject,
        body: bodyToSend,
        includeStaySubscribedButton,
      };

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${cookies['UserAuth'].token}`
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) throw new Error('Failed to send email');
      setSuccess(isAnnouncement && isScheduled ? 'Announcement email scheduled successfully!' : 'Email sent successfully!');

      // Reset state based on tab
      setSubject('');
      setBody('');
      setSelectedList('');
      setIncludeStaySubscribedButton(false);
      if (isAnnouncement) {
        setSelectedAnnouncementIds([]);
        setIsScheduled(false);
        setScheduleDate('');
      }
    } catch (err) {
      setError('Failed to send email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!cookies?.['UserAuth']?.token) {
      setError('Authentication token not found');
      return;
    }

    const isAnnouncement = tabValue === 1;

    if (isAnnouncement && isScheduled && !scheduleDate) {
      setError('Please choose a date to schedule this announcement for');
      return;
    }

    // Invisible when clean: only surface anything if Grok actually finds a problem.
    if (body.trim().length > 0) {
      setIsCheckingContent(true);
      try {
        const result = await contentProofreadingService.proofread(body, 'email', cookies['UserAuth'].token);
        if (result.hasIssues) {
          setProofreadIssues(result.issues);
          setProofreadDialogOpen(true);
          return;
        }
      } catch (err) {
        // Fail open - if the check itself can't be completed, don't block the send on it.
      } finally {
        setIsCheckingContent(false);
      }
    }

    await performSend(body);
  };

  const handleProofreadCancel = () => {
    setProofreadDialogOpen(false);
    setProofreadIssues([]);
  };

  const handleProofreadSendAnyway = () => {
    setProofreadDialogOpen(false);
    setProofreadIssues([]);
    performSend(body);
  };

  const handleProofreadApplyAndSend = (selectedIssues: ProofreadIssue[]) => {
    let corrected = body;
    selectedIssues.forEach(issue => {
      corrected = corrected.replace(issue.originalText, issue.suggestedText);
    });
    setBody(corrected);
    setProofreadDialogOpen(false);
    setProofreadIssues([]);
    performSend(corrected);
  };

  const handleRewrite = async () => {
    if (!body) {
      setError('Please write an email first to get suggestions');
      return;
    }

    if (!cookies?.['UserAuth']?.token) {
      setError('Authentication token not found');
      return;
    }

    setIsRewriting(true);
    setError('');
    setSuggestedBody('');

    try {
      const encodedContent = encodeURIComponent(body);
      const response = await fetch(`${baseBackendUrl}/api/email/suggest-rewrite?content=${encodedContent}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${cookies['UserAuth'].token}`
        }
      });

      if (!response.ok) throw new Error('Failed to get rewrite suggestion');
      const suggestion = await response.text();
      setSuggestedBody(suggestion);
    } catch (err) {
      setError('Failed to get rewrite suggestion. Please try again.');
    } finally {
      setIsRewriting(false);
    }
  };

  const handlePreview = async () => {
    if (!cookies?.['UserAuth']?.token) {
      setError('Authentication token not found');
      return;
    }

    setPreviewLoading(true);
    setError('');

    try {
      // Determine endpoint and payload based on tab
      const isAnnouncement = tabValue === 1;
      const endpoint = isAnnouncement
        ? `${baseBackendUrl}/api/email/preview/announcement`
        : `${baseBackendUrl}/api/email/preview/general`;

      const payload = isAnnouncement ? {
        title: subject || "Preview Title",
        body: body || "Preview Body",
        mailingListName: selectedList || "Preview List",
        announcementIds: selectedAnnouncementIds
      } : {
        mailingListName: selectedList || "Preview List",
        subject: subject || "Preview Subject",
        body: body || "Preview Body",
        includeStaySubscribedButton,
      };

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${cookies['UserAuth'].token}`
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) throw new Error('Failed to generate preview');
      const html = await response.text();
      setPreviewHtml(html);
      setPreviewOpen(true);
    } catch (err) {
      setError('Failed to generate preview. Please try again.');
    } finally {
      setPreviewLoading(false);
    }
  };

  if (!IsAdmin) {
    return (
      <Box sx={{ p: 3, pt: 10, textAlign: 'center' }}>
        <Typography variant="h5" color="error">Access Denied</Typography>
        <Typography>You do not have permission to access this page.</Typography>
      </Box>
    );
  }

  const filteredAnnouncements = announcements.filter(a =>
    a.title.toLowerCase().includes(announcementSearch.toLowerCase()) ||
    a.description.toLowerCase().includes(announcementSearch.toLowerCase())
  );

  return (
    <Box sx={{ p: 3, pt: 10, maxWidth: 1200, margin: '0 auto' }}>
      <Typography variant="h4" gutterBottom>Email Management</Typography>

      <Paper sx={{ mb: 4 }}>
        <Tabs value={tabValue} onChange={handleTabChange} centered>
          <Tab label="Regular Email" />
          <Tab label="Announcement Email" />
        </Tabs>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      <form onSubmit={handleSubmit}>
        <Stack spacing={3}>
          <FormControl fullWidth>
            <InputLabel>Mailing List</InputLabel>
            <Select
              value={selectedList}
              label="Mailing List"
              onChange={(e) => setSelectedList(e.target.value)}
              required
            >
              {mailingLists.map((list) => (
                <MenuItem key={list} value={list}>{list}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            fullWidth
            label="Email/Announcement Title"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            required
          />

          {tabValue === 0 && (
            <Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={includeStaySubscribedButton}
                    onChange={(e) => setIncludeStaySubscribedButton(e.target.checked)}
                  />
                }
                label={'Include a "Yes, keep me on the list" button'}
              />
              <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>
                For re-engagement emails. Clicks are recorded as opens, even when the reader's email app blocks images.
              </Typography>
            </Box>
          )}

          {tabValue === 1 && (
            <Box>
              <Box sx={{ mb: 3 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={isScheduled}
                      onChange={(e) => {
                        setIsScheduled(e.target.checked);
                        if (!e.target.checked) setScheduleDate('');
                      }}
                    />
                  }
                  label="Schedule for later instead of sending now"
                />
                {isScheduled && (
                  <Box sx={{ mt: 2, maxWidth: 300 }}>
                    <TextField
                      type="date"
                      label="Send Date"
                      value={scheduleDate}
                      onChange={(e) => setScheduleDate(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ min: new Date().toISOString().split('T')[0] }}
                      fullWidth
                      required
                    />
                    <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 0.5 }}>
                      Scheduled emails are sent once daily at 6:00 AM ET.
                    </Typography>
                  </Box>
                )}
              </Box>

              <Typography variant="h6" gutterBottom>Select Announcements</Typography>
              <Stack direction="row" spacing={3}>
                {/* Selection List */}
                <Box sx={{ flex: 1, border: '1px solid #ddd', borderRadius: 1, p: 2 }}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Search announcements..."
                    value={announcementSearch}
                    onChange={(e) => setAnnouncementSearch(e.target.value)}
                    sx={{ mb: 2 }}
                  />
                  <Box sx={{ maxHeight: 400, overflowY: 'auto' }}>
                    <List dense>
                      {filteredAnnouncements.map((ann) => (
                        <ListItem
                          key={ann.announcementId}
                          button
                          onClick={() => handleAnnouncementToggle(ann.announcementId)}
                        >
                          <Checkbox
                            edge="start"
                            checked={selectedAnnouncementIds.includes(ann.announcementId)}
                            tabIndex={-1}
                            disableRipple
                          />
                          <ListItemText
                            primary={ann.title}
                            secondary={ann.description.substring(0, 50) + '...'}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                </Box>

                {/* Ordered List (Drag & Drop) */}
                <Box sx={{ flex: 1, border: '1px solid #ddd', borderRadius: 1, p: 2 }}>
                  <Typography variant="subtitle2" gutterBottom color="textSecondary">
                    Selected Order (Drag handle to reorder)
                  </Typography>
                  <List dense>
                    {selectedAnnouncementIds.map((id, index) => {
                      const ann = announcements.find(a => a.announcementId === id);
                      if (!ann) return null;
                      return (
                        <ListItem
                          key={id}
                          draggable
                          onDragStart={(e) => handleDragStart(e, index)}
                          onDragOver={handleDragOver}
                          onDrop={(e) => handleDrop(e, index)}
                          sx={{
                            mb: 1,
                            bgcolor: '#f5f5f5',
                            borderRadius: 1,
                            cursor: 'move',
                            border: '1px solid transparent',
                            '&:hover': { border: '1px solid #3b82f6' }
                          }}
                        >
                          <ListItemIcon sx={{ minWidth: 40 }}>
                            <DragHandleIcon fontSize="small" />
                          </ListItemIcon>
                          <ListItemText primary={ann.title} />
                        </ListItem>
                      );
                    })}
                    {selectedAnnouncementIds.length === 0 && (
                      <Typography variant="body2" sx={{ textAlign: 'center', mt: 4, color: '#999' }}>
                        No announcements selected
                      </Typography>
                    )}
                  </List>
                </Box>
              </Stack>
            </Box>
          )}

          <Box>
            <Typography variant="subtitle1" gutterBottom>Email Body Content</Typography>
            <Editor
              apiKey="zpqa9rxf5ad7dx1bs1f3ium7xqej7sseo9fdslyvct3p568f"
              value={body}
              onEditorChange={(content: string) => {
                setBody(content);
                setSuggestedBody('');
              }}
              init={{
                height: 400,
                menubar: false,
                plugins: [
                  'advlist', 'autolink', 'lists', 'link', 'image', 'charmap', 'preview',
                  'anchor', 'searchreplace', 'visualblocks', 'code', 'fullscreen',
                  'insertdatetime', 'media', 'table', 'code', 'help', 'wordcount'
                ],
                toolbar: 'undo redo | formatselect | bold italic backcolor | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | removeformat | help',
                content_style: 'body { font-family:Helvetica,Arial,sans-serif; font-size:14px }'
              }}
            />
          </Box>

          <Stack direction="row" spacing={2}>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={loading || isCheckingContent || (tabValue === 1 && (selectedAnnouncementIds.length === 0 || (isScheduled && !scheduleDate)))}
              sx={{ minWidth: 150 }}
            >
              {loading || isCheckingContent
                ? <CircularProgress size={24} />
                : tabValue === 0
                  ? 'Send Email'
                  : isScheduled ? 'Schedule Announcement' : 'Send Announcement'}
            </Button>

            <Button
              variant="contained"
              onClick={handleRewrite}
              disabled={isRewriting || body.trim().length === 0}
              startIcon={<AutoFixHighIcon />}
              sx={{
                backgroundColor: '#6c757d',
                '&:hover': { backgroundColor: '#5a6268' },
                '&.Mui-disabled': { backgroundColor: '#e9ecef', color: '#adb5bd' }
              }}
            >
              {isRewriting ? <CircularProgress size={24} /> : 'AI Rewrite'}
            </Button>

            <Button
              variant="outlined"
              onClick={handlePreview}
              disabled={previewLoading || body.trim().length === 0}
            >
              {previewLoading ? <CircularProgress size={24} /> : 'Preview Content'}
            </Button>
          </Stack>

          {suggestedBody && (
            <Paper elevation={3} sx={{ p: 2, borderLeft: '4px solid #4caf50' }}>
              <Typography variant="h6" gutterBottom>AI Suggestion</Typography>
              <Box sx={{ mb: 2 }}>
                <div dangerouslySetInnerHTML={{ __html: suggestedBody }} />
              </Box>
              <Stack direction="row" spacing={2}>
                <Button variant="contained" color="success" onClick={() => { setBody(suggestedBody); setSuggestedBody(''); }}>
                  Accept Suggestion
                </Button>
                <Button variant="outlined" color="error" onClick={() => setSuggestedBody('')}>
                  Reject
                </Button>
              </Stack>
            </Paper>
          )}
        </Stack>
      </form>

      <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            Content Preview
            <IconButton onClick={() => setPreviewOpen(false)}><CloseIcon /></IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <div dangerouslySetInnerHTML={{ __html: previewHtml }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <ProofreadReviewDialog
        open={proofreadDialogOpen}
        issues={proofreadIssues}
        contentLabel="email"
        onCancel={handleProofreadCancel}
        onSendAnyway={handleProofreadSendAnyway}
        onApplyAndSend={handleProofreadApplyAndSend}
      />
    </Box>
  );
};

export default SendEmail; 